import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button } from '../atoms';
import { DataTable, StatCard, useToast } from '../organisms';
import { useEntityPermission } from '../lib/usePermissions';
import { formatDateTime } from '../lib/orgTimezone';

/**
 * QuickBooksSync — /v2/quickbooks (Domain 16.1)
 *
 * The operator's QBO sync console. Every route is live in
 * src/routes/integrations.js: PUSH (POST /api/workflow/invoices/:id/push-to-quickbooks),
 * the two-way PULL half (pull-payment-status / pull-all), the OAuth
 * connect/callback/status handshake, and the sync journal
 * (GET /api/workflow/quickbooks/sync-log). PUSH/PULL run against a fake/sandbox
 * QBO client until real Intuit creds (QBO_CLIENT_ID / QBO_CLIENT_SECRET) are set —
 * /status then reports not-connected and the banner shows SANDBOX, so a demo is
 * never mistaken for a live post. The pull actions still degrade gracefully if a
 * route is ever unreachable. Contract points in PRD §16.
 */

interface Invoice {
  id: string;
  invoice_number: string;
  client_id: string | null;
  amount: number | null;
  status: string;
  quickbooks_invoice_id: string | null;
}
interface ClientLite { id: string; name: string | null }
interface SyncLogEntry {
  id: string;
  entity_id: string;
  direction: 'push' | 'pull' | string;
  qbo_id: string | null;
  status: string | null;
  mode: 'fake' | 'live' | string | null;
  created_at: string;
}
interface PushResult {
  synced?: boolean;
  mode?: 'fake' | 'live';
  qbo_invoice_id?: string;
  link?: string;
  total?: number;
  reason?: string;
}

/** GET /api/integrations/quickbooks/status (src/routes/integrations.js). */
interface QboStatus {
  connected: boolean;
  mode: 'fake' | 'live' | string;
  /** True when the OAuth CONNECT creds resolve — gates the Connect button (16.2). */
  oauth_configured: boolean;
  realm_id: string | null;
  connected_at: string | null;
  last_sync_at: string | null;
  status: string;
}

// The OAuth callback bounces the browser back here with an outcome query param.
// Map each to a human message; a real handshake needs a live Intuit app + creds.
const QBO_ERROR_COPY: Record<string, string> = {
  consent_declined: 'QuickBooks connection was cancelled.',
  state: 'The connection request expired or was invalid — please try again.',
  missing_code: 'QuickBooks did not return an authorization code — please try again.',
  not_configured: 'QuickBooks OAuth is not configured on this environment yet.',
  exchange_failed: 'Could not complete the QuickBooks handshake — please try again.',
};

const money = (v: number | null) => (v != null ? `$${Math.round(v).toLocaleString()}` : '—');

// Defensive: the pull routes are live, so a 404/405 means the endpoint was
// temporarily unreachable (deploy in flight, proxy hiccup) — surface that softly
// rather than a hard error; any other failure shows the real message.
function pendingOrError(err: unknown, feature: string): { variant: 'info' | 'error'; message: string } {
  const unreachable = !(err instanceof ApiClientError) || err.status === 404 || err.status === 405;
  return unreachable
    ? { variant: 'info', message: `${feature} is temporarily unavailable — please try again in a moment.` }
    : { variant: 'error', message: (err as Error).message };
}

export function QuickBooksSync() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Push/pull are gated on invoice EXPORT per PRD §16.1 — the same capability
  // exportGate enforces on POST /api/workflow/invoices/:id/push-to-quickbooks.
  // This proxied to can_edit while can_export was missing from the permissions
  // payload; that hid the control from an agent, who HAS export but not edit.
  const canSync = useEntityPermission('invoice', 'can_export');

  const invoicesQuery = useQuery<Invoice[]>({
    queryKey: ['invoices-list'],
    queryFn: ({ signal }) => api.get<Invoice[]>('/api/invoices', signal),
  });
  const clientsQuery = useQuery<ClientLite[]>({
    queryKey: ['clients-list'],
    queryFn: ({ signal }) => api.get<ClientLite[]>('/api/clients', signal),
  });
  const logQuery = useQuery<SyncLogEntry[]>({
    queryKey: ['quickbooks-sync-log'],
    queryFn: ({ signal }) => api.get<SyncLogEntry[]>('/api/workflow/quickbooks/sync-log', signal),
  });

  const push = useMutation<PushResult, Error, string>({
    mutationFn: (invoiceId) => api.post<PushResult>(`/api/workflow/invoices/${invoiceId}/push-to-quickbooks`, {}),
    onSuccess: (res) => {
      const where = res.mode === 'live' ? 'QuickBooks' : 'QuickBooks (sandbox)';
      toast({ variant: 'success', message: res.synced ? `Pushed to ${where}.` : `Push skipped: ${res.reason || 'already synced'}.` });
      queryClient.invalidateQueries({ queryKey: ['invoices-list'] });
      queryClient.invalidateQueries({ queryKey: ['quickbooks-sync-log'] });
    },
    onError: (err) => {
      const b = err instanceof ApiClientError ? (err.body as { error?: string }) : undefined;
      toast({ variant: 'error', message: b?.error || err.message });
    },
  });

  // Connection status (GET /api/integrations/quickbooks/status) — drives the
  // banner: live-connected vs sandbox/fake mode. Never exposes tokens.
  const statusQuery = useQuery<QboStatus>({
    queryKey: ['quickbooks-status'],
    queryFn: ({ signal }) => api.get<QboStatus>('/api/integrations/quickbooks/status', signal),
  });

  // OAuth CONNECT (16.2): a full-page navigation to /connect. The backend gates on
  // invoice EXPORT, mints a signed state, and 302-redirects to Intuit's consent
  // surface; the callback returns the browser here with an outcome query param.
  const startConnect = () => { window.location.href = '/api/integrations/quickbooks/connect'; };

  // Surface the callback outcome (?qbo=connected / ?qbo_error=…), refresh the
  // connection status, then strip the param so a refresh doesn't re-toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ok = params.get('qbo');
    const err = params.get('qbo_error');
    if (!ok && !err) return;
    if (ok === 'connected') {
      toast({ variant: 'success', message: 'QuickBooks company connected.' });
      queryClient.invalidateQueries({ queryKey: ['quickbooks-status'] });
    } else if (err) {
      toast({ variant: 'error', message: QBO_ERROR_COPY[err] || 'Could not connect to QuickBooks.' });
    }
    params.delete('qbo'); params.delete('qbo_error');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pullAll = useMutation<unknown, Error>({
    mutationFn: () => api.post('/api/integrations/quickbooks/pull-all', {}),
    onSuccess: () => {
      toast({ variant: 'success', message: 'Payment status pulled from QuickBooks.' });
      queryClient.invalidateQueries({ queryKey: ['invoices-list'] });
      queryClient.invalidateQueries({ queryKey: ['quickbooks-sync-log'] });
    },
    onError: (err) => toast(pendingOrError(err, 'Pulling payment status')),
  });

  const clientName = useMemo(() => {
    const map = new Map((clientsQuery.data ?? []).map((c) => [c.id, c.name]));
    return (id: string | null) => (id ? map.get(id) || '—' : '—');
  }, [clientsQuery.data]);

  const columns = useMemo<ColumnDef<Invoice>[]>(() => [
    { accessorKey: 'invoice_number', header: 'Invoice #', cell: ({ getValue }) => <span style={{ fontFamily: 'var(--font-mono)' }}>{(getValue() as string) || '—'}</span> },
    { id: 'client', header: 'Client', cell: ({ row }) => clientName(row.original.client_id) },
    { accessorKey: 'amount', header: 'Amount', cell: ({ getValue }) => money(getValue() as number | null) },
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <Badge variant="neutral">{String(getValue() || '').replace(/_/g, ' ')}</Badge> },
    {
      id: 'qbo',
      header: 'QuickBooks',
      cell: ({ row }) => row.original.quickbooks_invoice_id
        ? <Badge variant="success" dot>Synced</Badge>
        : <Badge variant="neutral">Not synced</Badge>,
    },
    {
      id: 'action',
      header: '',
      cell: ({ row }) => canSync ? (
        <Button
          variant={row.original.quickbooks_invoice_id ? 'ghost' : 'secondary'}
          size="sm"
          loading={push.isPending && push.variables === row.original.id}
          // stopPropagation so pushing doesn't also trigger the row's navigation.
          onClick={(e) => { e.stopPropagation(); push.mutate(row.original.id); }}
        >
          {row.original.quickbooks_invoice_id ? 'Re-push' : 'Push to QuickBooks'}
        </Button>
      ) : null,
    },
  ], [canSync, push.isPending, push.variables, clientName]);

  if (invoicesQuery.isLoading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading invoices…</p>;
  if (invoicesQuery.error) {
    return (
      <div style={{
        padding: 'var(--sp-4)', background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)', borderRadius: 'var(--r-lg)',
      }}>{(invoicesQuery.error as Error).message}</div>
    );
  }

  const invoices = invoicesQuery.data ?? [];
  const syncedCount = invoices.filter((i) => i.quickbooks_invoice_id).length;

  return (
    <>
      <header style={{ marginBottom: 'var(--sp-5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--sp-4)', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 'var(--fs-3xl)', fontWeight: 'var(--fw-semibold)', letterSpacing: 'var(--ls-tight)', color: 'var(--color-ink)' }}>QuickBooks sync</h1>
          <p style={{ margin: 'var(--sp-1) 0 0', color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)' }}>
            Push invoices to QuickBooks and pull payment status back.
          </p>
        </div>
        {canSync && (
          <Button variant="secondary" size="sm" loading={pullAll.isPending} onClick={() => pullAll.mutate()}>
            Sync all payments
          </Button>
        )}
      </header>

      {/* Connection banner — driven by GET /api/integrations/quickbooks/status.
          A persisted per-org connection (OAuth, 16.2) shows green; otherwise the
          sandbox notice stays loud so a demo isn't mistaken for a live post, and
          the Connect button starts the consent flow when OAuth is configured. */}
      {statusQuery.data?.connected ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
          padding: 'var(--sp-3) var(--sp-4)', marginBottom: 'var(--sp-5)',
          background: 'var(--color-status-green-bg)', border: '1px solid var(--color-status-green)',
          borderRadius: 'var(--r-lg)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink)',
        }}>
          <Badge variant="success" dot>Connected</Badge>
          <span style={{ flex: 1 }}>
            Live QuickBooks company{statusQuery.data.realm_id ? ` (realm ${statusQuery.data.realm_id})` : ''} connected.
            {statusQuery.data.last_sync_at ? ` Last payment pull ${formatDateTime(statusQuery.data.last_sync_at)}.` : ''}
          </span>
          {canSync && statusQuery.data.oauth_configured && (
            <Button variant="ghost" size="sm" onClick={startConnect}>Reconnect</Button>
          )}
        </div>
      ) : (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
          padding: 'var(--sp-3) var(--sp-4)', marginBottom: 'var(--sp-5)',
          background: 'var(--color-status-amber-bg)', border: '1px solid var(--color-status-amber)',
          borderRadius: 'var(--r-lg)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink)',
        }}>
          <Badge variant="warning" dot>Sandbox</Badge>
          <span style={{ flex: 1 }}>
            {statusQuery.data?.oauth_configured
              ? 'Pushes are simulated against a sandbox company. Connect a live QuickBooks company to post for real.'
              : 'Pushes are simulated against a sandbox company, not posted to a live QuickBooks. Connecting a live company requires an Intuit app to be configured for this environment.'}
          </span>
          {canSync && statusQuery.data?.oauth_configured && (
            <Button variant="primary" size="sm" onClick={startConnect}>Connect QuickBooks</Button>
          )}
        </div>
      )}

      <div style={{ marginBottom: 'var(--sp-5)' }}>
        <StatCard label="Synced invoices" value={`${syncedCount} / ${invoices.length}`} caption="pushed to QuickBooks" accent="hunter" />
      </div>

      {invoices.length === 0 ? (
        <Empty message="No invoices to sync yet." />
      ) : (
        <DataTable data={invoices} columns={columns} onRowClick={(inv) => navigate(`/invoices/${inv.id}`)} />
      )}

      <div style={{ marginTop: 'var(--sp-7)' }}>
        <h2 style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xl)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Sync journal</h2>
        <SyncLog entries={logQuery.data ?? []} loading={logQuery.isLoading} />
      </div>
    </>
  );
}

function SyncLog({ entries, loading }: { entries: SyncLogEntry[]; loading: boolean }) {
  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading sync journal…</p>;
  if (entries.length === 0) return <Empty message="No sync activity yet. Push an invoice to start the journal." />;
  return (
    <div style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', overflow: 'hidden' }}>
      {entries.map((e) => (
        <div key={e.id} style={{
          display: 'flex', alignItems: 'center', gap: 'var(--sp-3)',
          padding: 'var(--sp-3) var(--sp-4)', borderBottom: '1px solid var(--color-border)', fontSize: 'var(--fs-sm)',
        }}>
          <Badge variant={e.direction === 'pull' ? 'info' : 'neutral'}>{e.direction}</Badge>
          <Badge variant={e.status === 'synced' || e.status === 'success' ? 'success' : e.status === 'error' ? 'danger' : 'neutral'} dot>
            {e.status || 'unknown'}
          </Badge>
          {e.mode === 'fake' && <Badge variant="warning">sandbox</Badge>}
          <span style={{ color: 'var(--color-ink-3)', fontFamily: 'var(--font-mono)' }}>{e.entity_id.slice(0, 8)}</span>
          <span style={{ marginLeft: 'auto', color: 'var(--color-muted)' }}>{formatDateTime(e.created_at)}</span>
        </div>
      ))}
    </div>
  );
}

function Empty({ message }: { message: string }) {
  return (
    <div style={{
      padding: 'var(--sp-6)', background: 'var(--color-card)', border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)', color: 'var(--color-muted)', textAlign: 'center', fontSize: 'var(--fs-md)',
    }}>{message}</div>
  );
}
